import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PX7AuthOverlay, createBackendTransport } from "@px7digital/system-libraries/auth-overlay";
import { useAuth } from "../app/AuthContext";

// In-app PX7 Account sign-in. The overlay collects email/password in
// BeforeMainstream's own UI and POSTs to our BFF endpoints, which broker the
// accounts call server-to-server and set the bm_session cookie — no bounce to
// accounts.px7. The classic SSO redirect stays as a fallback link.
const px7AuthTransport = createBackendTransport({
  loginUrl: "/api/auth/px7/login",
  signupUrl: "/api/auth/px7/signup",
});

// Theme the shared overlay entirely from BeforeMainstream's own CSS variables
// so it reads as native (the app is a single dark palette).
const px7AuthTheme = {
  font: "inherit",
  surface: "var(--bg-card)",
  ink: "var(--fg)",
  muted: "var(--fg-muted)",
  border: "var(--border)",
  fieldBg: "var(--bg-elevated)",
  fieldBorder: "var(--border-hot)",
  fieldRadius: "var(--radius)",
  accent: "var(--accent)",
  accentInk: "var(--accent-ink)",
  accentHover: "var(--accent-dim)",
  radius: "var(--radius-lg)",
  noticeBg: "var(--bg-elevated)",
};

export function LoginPage() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("return") || "/";

  // Auto-bounce signed-in users back where they came from.
  useEffect(() => {
    if (!loading && user) navigate(returnTo, { replace: true });
  }, [loading, user, returnTo, navigate]);

  // Classic SSO redirect, preserved as the overlay's fallback. Plain relative
  // URL — the Worker resolves the callback against the request origin.
  const ssoFallbackHref = useMemo(() => {
    if (returnTo && returnTo !== "/") {
      return `/api/auth/accounts/start?return=${encodeURIComponent(returnTo)}`;
    }
    return "/api/auth/accounts/start";
  }, [returnTo]);

  // While loading the session OR while bouncing a signed-in user away, show a
  // single calm state instead of flashing the overlay.
  if (loading || user) {
    return (
      <div className="container empty-state" aria-live="polite">
        {user ? "Signing you in…" : "Checking your session…"}
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingBlock: "1.5rem", maxWidth: 480 }}>
      <PX7AuthOverlay
        inline
        appName="BeforeMainstream"
        transport={px7AuthTransport}
        theme={px7AuthTheme}
        initialMode="login"
        minPasswordLength={8}
        copy={{
          login: {
            subtitle:
              "Sign in to save brands and submit new discoveries.",
          },
          create: {
            subtitle:
              "Create your account.",
          },
        }}
        footer="Sign out everywhere anytime"
        ssoFallbackHref={ssoFallbackHref}
        ssoFallbackLabel="Having trouble? Use the classic sign-in"
        onAuthenticated={async () => {
          // Re-probe the BFF session so AuthContext.user settles exactly as on
          // a fresh page load; the effect above then navigates to returnTo.
          await refresh();
          navigate(returnTo, { replace: true });
        }}
      />
    </div>
  );
}
